import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import '../styles/Slider.css';
import backendUrl from '../BackendUrl';

const ANIMATION_DURATION = 500; // ms

const Slider = () => {
  const [slides, setSlides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentSlide, setCurrentSlide] = useState(1); // Start at 1 (first real slide)
  const [infoAnim, setInfoAnim] = useState(false);
  const [paused, setPaused] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const trackRef = useRef(null);
  const timeoutRef = useRef(null);
  const slideInterval = 7000;

  // Prepare slides with clones for infinite effect
  const getLoopSlides = (arr) => {
    if (arr.length === 0) return [];
    return [arr[arr.length - 1], ...arr, arr[0]];
  };
  const loopSlides = getLoopSlides(slides);

  useEffect(() => {
    axios
      .get(`${backendUrl}/api/sliders/get-slider`)
      .then((response) => {
        if (response.data && response.data.length > 0) {
          setSlides(response.data);
          setCurrentSlide(1); // Start at first real slide
        }
        setLoading(false);
      })
      .catch((error) => {
        console.error('Error fetching slider info:', error);
        setLoading(false);
      });
  }, []);

  // Auto-advance
  useEffect(() => {
    if (slides.length === 0 || paused) return;
    timeoutRef.current = setTimeout(() => {
      handleNext();
    }, slideInterval);
    return () => clearTimeout(timeoutRef.current);
  }, [currentSlide, slides.length, paused]);

  // Info animation
  useEffect(() => {
    setInfoAnim(true);
    const timer = setTimeout(() => setInfoAnim(false), ANIMATION_DURATION);
    return () => clearTimeout(timer);
  }, [currentSlide]);

  // Handle transition end for infinite loop
  const handleTransitionEnd = () => {
    setIsTransitioning(false);
    if (currentSlide === 0) {
      setCurrentSlide(slides.length);
      if (trackRef.current) {
        trackRef.current.style.transition = 'none';
        trackRef.current.style.transform = `translateX(-${slides.length * 100}%)`;
        void trackRef.current.offsetWidth;
        trackRef.current.style.transition = `transform ${ANIMATION_DURATION}ms cubic-bezier(0.77, 0, 0.175, 1)`;
      }
    } else if (currentSlide === slides.length + 1) {
      setCurrentSlide(1);
      if (trackRef.current) {
        trackRef.current.style.transition = 'none';
        trackRef.current.style.transform = `translateX(-100%)`;
        void trackRef.current.offsetWidth;
        trackRef.current.style.transition = `transform ${ANIMATION_DURATION}ms cubic-bezier(0.77, 0, 0.175, 1)`;
      }
    }
  };

  const handleNext = () => {
    if (isTransitioning || slides.length === 0) return;
    setIsTransitioning(true);
    setCurrentSlide((prev) => prev + 1);
  };

  const handlePrev = () => {
    if (isTransitioning || slides.length === 0) return;
    setIsTransitioning(true);
    setCurrentSlide((prev) => prev - 1);
  };

  const goToSlide = (index) => {
    if (isTransitioning || slides.length === 0) return;
    setIsTransitioning(true);
    setCurrentSlide(index + 1); // +1 because of the prepended clone
  };

  // Calculate transform (each image is 100% width)
  const trackStyle = {
    transform: `translateX(-${currentSlide * 100}%)`,
    transition: isTransitioning
      ? `transform 0.5s cubic-bezier(0.77, 0, 0.175, 1)`
      : 'none',
    width: `${loopSlides.length * 100}%`,
  };

  // Calculate real index for info/dots
  const realIndex = (currentSlide - 1 + slides.length) % slides.length;

  return (
    <div className="slider-container">
      {loading ? (
        <div>Loading...</div>
      ) : slides.length === 0 ? (
        <div>No sliders available.</div>
      ) : (
        <>
          <div className="slider-info">
            <div>
              <h2 className={infoAnim ? "info-animate" : ""}>
                {slides[realIndex]?.slider}
              </h2>
              <p className={infoAnim ? "info-animate" : ""}>
                {slides[realIndex]?.description}
              </p>
            </div>
            <div>
              <div className="slider-controls">
                <button className="arrow left-arrow" onClick={handlePrev} disabled={isTransitioning}>
                  ◀
                </button>
                <div className="dots">
                  {slides.map((_, index) => (
                    <button
                      key={index}
                      className={`slider-dot ${realIndex === index ? 'active' : ''}`}
                      onClick={() => goToSlide(index)}
                      disabled={isTransitioning}
                    />
                  ))}
                </div>
                <button className="arrow right-arrow" onClick={handleNext} disabled={isTransitioning}>
                  ▶
                </button>
                <button
                  className="pause-btn"
                  onClick={() => setPaused((prev) => !prev)}
                  aria-label={paused ? 'Play slider' : 'Pause slider'}
                >
                  {paused ? '▶' : '⏸'}
                </button>
              </div>
            </div>
          </div>
          <div className="slider-image-box">
            <div
              className="slider-track"
              ref={trackRef}
              style={trackStyle}
              onTransitionEnd={handleTransitionEnd}
            >
              {loopSlides.map((slide, idx) => (
                <img
                  key={idx}
                  src={slide.sliderImg || 'default-slider-image-url'}
                  alt={slide.slider}
                  className="slider-image"
                />
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Slider;